import React from "react";
import { ArrowDown, Rocket } from "lucide-react";
import { heroData } from "../data/mock";

export default function Hero() {
  return (
    <section id="hero" className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-10 overflow-hidden">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FFF8E7] border border-[#0F0F10]/20 text-sm font-medium">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            <span>{heroData.greeting}</span>
            <span className="text-base">✋</span>
          </div>

          <h1 className="mt-6 text-[56px] sm:text-[80px] lg:text-[104px] leading-[0.95] font-extrabold tracking-tight">
            I'M
            <br />
            <span className="relative inline-block">
              <span className="squiggle">{heroData.name.toUpperCase()}</span>
            </span>
          </h1>

          <p className="mt-8 text-lg sm:text-xl text-[#0F0F10]/80 max-w-2xl leading-relaxed">
            Titles? Sure, I've had a few:&nbsp;
            <span className="font-serif-italic text-[#0F0F10]">entrepreneur</span>,&nbsp;
            <span className="font-serif-italic text-[#0F0F10]">consultant</span>… but my real job?{' '}
            <span className="font-bold text-blue-700 inline-flex items-center gap-1">
              Product Manager <Rocket className="w-5 h-5 text-orange-500" />
            </span>
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <button
              onClick={() => document.getElementById("work")?.scrollIntoView({ behavior: "smooth" })}
              className="btn-yellow"
            >
              See my work
              <ArrowDown className="w-4 h-4" />
            </button>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-[#0F0F10] font-semibold hover:bg-[#0F0F10] hover:text-[#FFE357] transition-colors"
            >
              Get in touch
            </a>
          </div>

          <div className="mt-14 inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-[#FFF8E7] border-[1.5px] border-[#0F0F10]/40">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#0F0F10]/60">
              Companies I built products for
            </span>
          </div>
          <div className="mt-4 flex flex-wrap gap-x-8 gap-y-3 items-center text-[#0F0F10]/80">
            <span className="text-lg font-bold tracking-tight">Bristol Myers Squibb</span>
            <span className="text-[#0F0F10]/30">•</span>
            <span className="text-lg font-extrabold tracking-tight">ZS</span>
            <span className="text-[#0F0F10]/30">•</span>
            <span className="text-lg font-bold tracking-tight">Opentalk</span>
            <span className="text-[#0F0F10]/30">•</span>
            <span className="text-lg font-bold tracking-tight">Hope</span>
            <span className="text-[#0F0F10]/30">•</span>
            <span className="text-lg font-bold tracking-tight">Okta</span>
          </div>
        </div>

        <div className="lg:col-span-5 relative">
          <div className="relative aspect-[4/5] max-w-[460px] mx-auto">
            <div className="absolute -inset-6 rounded-[40px] bg-gradient-to-br from-[#5C3A29] to-[#3A2418] -z-10 rotate-3"></div>
            <div className="absolute -inset-2 rounded-[36px] bg-[#FFE357] -z-10 -rotate-2"></div>

            <div className="relative w-full h-full rounded-[32px] overflow-hidden border-[2px] border-[#0F0F10] shadow-[8px_8px_0_#0F0F10] bg-[#FFF8E7]">
              <img
                src={heroData.imageUrl}
                alt="Tharun Sai"
                className="w-full h-full object-cover"
              />

              {/* top live indicator */}
              <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
                <span className="font-mono text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-[#FFF8E7]/90 backdrop-blur border border-[#0F0F10]/30 text-[#0F0F10]">
                  / currently
                </span>
                <span className="inline-flex items-center gap-1.5 font-mono text-[11px] px-2.5 py-1 rounded-full bg-[#FFF8E7]/90 backdrop-blur border border-[#0F0F10]/30 text-[#0F0F10]">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                  shipping
                </span>
              </div>

              {/* bottom info bar */}
              <div className="absolute inset-x-0 bottom-0 px-5 pt-16 pb-5 bg-gradient-to-t from-black/85 via-black/55 to-transparent text-white">
                <div className="flex items-end justify-between gap-3">
                  <div>
                    <div className="font-mono text-[11px] uppercase tracking-widest text-white/70">PM · ISB Co'25</div>
                    <div className="text-2xl font-bold tracking-tight">Tharun Sai</div>
                  </div>
                  <div className="text-right">
                    <div className="font-extrabold leading-none text-[44px] text-[#FFE357]">
                      5<span className="text-[#FFE357]">+</span>
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-white/70 mt-1">
                      years shipping
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute -top-6 -left-6 px-4 py-2 rounded-full bg-[#FFF8E7] border-[1.5px] border-[#0F0F10] shadow-[3px_3px_0_#0F0F10] text-sm font-semibold rotate-[-8deg]">
              🚀 5+ yrs shipping
            </div>
            <div className="absolute -bottom-4 -right-2 px-4 py-2 rounded-full bg-[#0F0F10] text-[#FFE357] text-sm font-semibold rotate-[6deg]">
              4 products + 2 lessons
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
